import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Plus, Folder, Search, Star, ChevronDown } from "lucide-react";

const StoreSidebar = ({ isOpen, onClose, dynamicCategories = [], products = [], onFilterChange }) => {
  const navigate = useNavigate();
  const [openCategory, setOpenCategory] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState({ price: true, rating: true });
  const [localSearch, setLocalSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  
  // Dual-handle movable price ranges
  const [priceFrom, setPriceFrom] = useState(0);
  const [priceTo, setPriceTo] = useState(100000);
  const [selectedRating, setSelectedRating] = useState(null);

  // Sync real-time lookup list inside the sidebar incorporating all filters
  useEffect(() => {
    const term = localSearch.toLowerCase().trim();
    if (term) {
      const filtered = products.filter((p) => {
        // 1. Filter by Name or Category
        const matchesSearch = p.name.toLowerCase().includes(term) || p.category?.toLowerCase().includes(term);
        
        // 2. Filter by Dynamic Price Range
        const matchesPrice = p.price >= priceFrom && p.price <= priceTo;
        
        // 3. Filter by Star Rating (Assuming p.rating or p.reviewScore holds the value)
        const productRating = p.rating || parseFloat(p.reviewScore) || 5; 
        const matchesRating = selectedRating ? productRating >= selectedRating : true;

        return matchesSearch && matchesPrice && matchesRating;
      });
      setSearchResults(filtered.slice(0, 6));
    } else {
      setSearchResults([]);
    }
  }, [localSearch, products, priceFrom, priceTo, selectedRating]);

  const handleSearchChange = (e) => {
    setLocalSearch(e.target.value);
    onFilterChange?.({ type: "search", value: e.target.value });
  };

  const handleMinSlider = (e) => {
    const val = Math.min(parseInt(e.target.value), priceTo - 1000);
    setPriceFrom(val);
    onFilterChange?.({ type: "priceRange", from: val, to: priceTo });
  };

  const handleMaxSlider = (e) => {
    const val = Math.max(parseInt(e.target.value), priceFrom + 1000);
    setPriceTo(val);
    onFilterChange?.({ type: "priceRange", from: priceFrom, to: val });
  };

  const handleRatingClick = (rating) => {
    const newValue = selectedRating === rating ? null : rating;
    setSelectedRating(newValue);
    onFilterChange?.({ type: "rating", value: newValue });
  };

  // 1. The conditional logic check sits directly before mounting the final render lifecycle loop
  if (!isOpen) return null;

  return (
    <>
      {/* 2. Full-Screen Backdrop Overlay Layer to instantly handle automated closing clicks */}
      <div 
        className="fixed inset-0 bg-black/65 backdrop-blur-xs z-40 transition-opacity cursor-pointer"
        onClick={onClose}
      />

      {/* 3. The main sidebar panel set to z-50 to rest cleanly above the interactive overlay layer */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 bg-[#002B26] text-white flex flex-col justify-between transform transition-transform duration-300 ease-in-out border-r border-[#003d36] translate-x-0 h-screen shrink-0`}>
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {/* Header */}
          <div className="p-5 flex items-center justify-between border-b border-b-white/10">
            <div>
              <h2 className="text-xl font-black tracking-wide text-white">PRONEST</h2>
              <p className="text-[10px] text-emerald-500/70 tracking-widest uppercase mt-0.5">PRONEST.IN</p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:text-white transition-colors">
              <X size={16} />
            </button>
          </div>

          {/* Sidebar Live Search with Dropdown Results */}
          <div className="px-6 pt-6 relative">
            <p className="text-[10px] font-bold text-[#cca453] tracking-widest uppercase opacity-80 mb-2">SEARCH PRODUCTS</p>
            <div className="relative flex items-center bg-white/5 border border-white/10 rounded-xl px-3 py-2 focus-within:border-[#cca453]/50 transition-colors">
              <Search size={16} className="text-slate-400 mr-2" />
              <input 
                type="text" 
                placeholder="Search products..." 
                value={localSearch}
                onChange={handleSearchChange}
                className="w-full bg-transparent text-xs text-white outline-none placeholder-slate-500"
              />
            </div>

            {/* Sidebar Search Dropdown Panel (Filtered dynamically) */}
            {searchResults.length > 0 && (
              <div className="absolute left-6 right-6 z-30 mt-1 max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-[#001f1b] shadow-2xl p-1 space-y-0.5">
                {searchResults.map((product) => (
                  <button
                    key={product._id}
                    onClick={() => {
                      navigate(`/product/${product._id}`);
                      setLocalSearch("");
                      onClose();
                    }}
                    className="flex w-full items-center gap-2.5 p-2 rounded-lg text-left hover:bg-white/5 transition-colors group"
                  >
                    <div className="h-7 w-7 flex-shrink-0 rounded-md overflow-hidden bg-white/10">
                      <img src={product.image || 'https://via.placeholder.com/40'} alt={product.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[11px] font-medium text-slate-200 group-hover:text-white">{product.name}</p>
                      <p className="text-[9px] text-slate-400 font-semibold">₹{product.price}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Filters Section */}
          <div className="px-6 pt-6 space-y-5">
            
            {/* Dual Handle Movable Price Range */}
            <div className="border-b border-white/5 pb-4">
              <button onClick={() => setFiltersOpen(p => ({...p, price: !p.price}))} className="w-full flex items-center justify-between text-[10px] font-bold text-[#cca453] tracking-widest uppercase opacity-80 mb-2">
                <span>PRICE RANGE</span>
                <ChevronDown size={14} className={`transform transition-transform text-slate-400 ${filtersOpen.price ? "rotate-180" : ""}`} />
              </button>
              
              {filtersOpen.price && (
                <div className="mt-3 px-1">
                  <div className="relative h-1 bg-white/10 rounded-lg">
                    <input 
                      type="range" 
                      min="0" 
                      max="100000" 
                      step="1000"
                      value={priceFrom} 
                      onChange={handleMinSlider}
                      className="absolute pointer-events-none appearance-none bg-transparent w-full h-1 top-0 left-0 accent-[#cca453] [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#cca453] [&::-webkit-slider-thumb]:appearance-none cursor-pointer"
                    />
                    <input 
                      type="range" 
                      min="0" 
                      max="100000" 
                      step="1000"
                      value={priceTo} 
                      onChange={handleMaxSlider}
                      className="absolute pointer-events-none appearance-none bg-transparent w-full h-1 top-0 left-0 accent-[#cca453] [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#cca453] [&::-webkit-slider-thumb]:appearance-none cursor-pointer"
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-slate-400 mt-3 font-mono">
                    <span>From: <b className="text-white">₹{priceFrom.toLocaleString("en-IN")}</b></span>
                    <span>To: <b className="text-[#cca453]">₹{priceTo.toLocaleString("en-IN")}</b></span>
                  </div>
                </div>
              )}
            </div>

            {/* Ratings Filter Accordion */}
            <div className="border-b border-white/5 pb-3">
              <button onClick={() => setFiltersOpen(p => ({...p, rating: !p.rating}))} className="w-full flex items-center justify-between text-[10px] font-bold text-[#cca453] tracking-widest uppercase opacity-80 mb-2">
                <span>CUSTOMER RATINGS</span>
                <ChevronDown size={14} className={`transform transition-transform text-slate-400 ${filtersOpen.rating ? "rotate-180" : ""}`} />
              </button>
              
              {filtersOpen.rating && (
                <div className="flex flex-col gap-1.5 mt-2">
                  {[5, 4].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => handleRatingClick(rating)}
                      className={`flex items-center justify-between text-left text-xs px-3 py-2 rounded-lg transition-colors border ${
                        selectedRating === rating ? "bg-[#cca453]/10 border-[#cca453] text-white font-bold" : "bg-white/5 border-transparent text-slate-300 hover:bg-white/10"
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        {Array.from({ length: rating }).map((_, i) => <Star key={i} size={12} className="fill-[#cca453] text-[#cca453]" />)}
                        {Array.from({ length: 5 - rating }).map((_, i) => <Star key={i} size={12} className="text-slate-600" />)}
                        <span className="ml-1 text-[11px]">{rating} Star {rating === 5 ? "" : "& Up"}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Categories List */}
          <div className="px-4 pt-4 pb-4">
            <p className="px-2 text-[10px] font-bold text-[#cca453] tracking-widest uppercase opacity-80 mb-2">CATEGORIES</p>
            <div className="space-y-0.5">
              {dynamicCategories.map((catName) => {
                const isCatOpen = openCategory === catName;
                return (
                  <div key={catName} className="border-b border-[#003d36]/40 last:border-0">
                    <button onClick={() => setOpenCategory(isCatOpen ? null : catName)} className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-slate-200 font-semibold text-sm hover:bg-white/5 transition-colors text-left">
                      <div className="flex items-center gap-3 min-w-0">
                        <Folder size={16} className="text-[#cca453] shrink-0" />
                        <span className="truncate capitalize text-xs">{catName}</span>
                      </div>
                      <span className={`text-slate-400 transform transition-transform text-[9px] ml-1 shrink-0 ${isCatOpen ? "rotate-180" : ""}`}>▼</span>
                    </button>
                    {isCatOpen && (
                      <div className="pl-10 pr-3 pb-2 bg-black/10 space-y-1 text-xs text-slate-400">
                        <Link to={`/store?category=${encodeURIComponent(catName)}`} onClick={onClose} className="block py-1 hover:text-white transition-colors">View all in {catName}</Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="p-4 bg-[#002420] border-t border-[#003d36]">
          <button className="w-full flex items-center justify-center gap-2 bg-[#cca453] hover:bg-[#b89244] text-[#002B26] font-bold text-xs py-2.5 px-4 rounded-xl transition-all shadow-md active:scale-[0.98]">
            <Plus size={14} strokeWidth={3} />
            <span>Register Your Business</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default StoreSidebar;